import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { buildFinancialApiUrl } from '../utils/financial-api-url.util';
import { ErrorService } from '../shared/services/error.service';
import { 
  AccountsPayable, 
  AccountsPayableCreate,
  AccountsPayableDepositRefundCreate,
  AccountsPayableUpdate,
  AccountsPayableFilter,
  AccountsPayableSummary,
  AccountsPayableBatchCreateResponse
} from '../models/AccountsPayable';
import { AccountsPayableAttachment } from '../models/AccountsPayableAttachment';

export interface AccountsPayablePagedResult {
  items: AccountsPayable[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface PaginationOptions {
  page?: number;
  pageSize?: number;
}

export interface AccountsPayableFilters extends PaginationOptions {
  supplierId?: number;
  status?: string;
  costCenterId?: number;
  category?: string;
  dueDateStart?: string;
  dueDateEnd?: string;
  paymentDateStart?: string;
  paymentDateEnd?: string;
  searchTerm?: string;
  search?: string; // Alias para searchTerm conforme documentação
}

@Injectable({
  providedIn: 'root'
})
export class AccountsPayableService {

  private financialUrl(path = ''): string {
    return buildFinancialApiUrl('accountspayable', path);
  }

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  // ===== CRUD OPERATIONS =====

  createAccountsPayable(accountsPayable: AccountsPayableCreate): Observable<AccountsPayable> {
    return this.http.post<any>(this.financialUrl(), accountsPayable).pipe(
      map(resp => this.mapAccountsPayable(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao criar conta a pagar');
        return throwError(() => error);
      })
    );
  }

  /**
   * Cria vários títulos em lote (parcelas e/ou rateio por centro de custo).
   * POST /api/accountspayable/batch?createdBy=...
   */
  createAccountsPayableBatch(
    batch: AccountsPayableCreate[],
    createdBy?: string
  ): Observable<AccountsPayableBatchCreateResponse> {
    let params = new HttpParams();
    if (createdBy?.trim()) {
      params = params.set('createdBy', createdBy.trim());
    }
    return this.http
      .post<AccountsPayableBatchCreateResponse | AccountsPayable[]>(this.financialUrl('/batch'), batch, { params })
      .pipe(
        map((res) => {
          if (Array.isArray(res)) {
            return { count: res.length, items: res.map(r => this.mapAccountsPayable(r)) };
          }
          const items = (res?.items ?? res?.created ?? []).map((r: any) => this.mapAccountsPayable(r));
          return { count: res?.count ?? items.length, items };
        }),
        catchError(error => {
          const msg = error?.error?.message || 'Erro ao criar contas a pagar em lote';
          this.errorService.showErrorMessage(msg);
          return throwError(() => error);
        })
      );
  }

  /** Extorno de caução vinculado a contrato B2C (contractDraftId) ou B2B (b2BContractId). */
  createDepositRefund(payload: AccountsPayableDepositRefundCreate): Observable<AccountsPayable> {
    return this.http.post<any>(this.financialUrl(), payload).pipe(
      map(resp => this.mapAccountsPayable(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao registrar extorno de caução');
        return throwError(() => error);
      })
    );
  }

  getAccountsPayableById(id: number): Observable<AccountsPayable> {
    return this.http.get<any>(this.financialUrl(`/${id}`)).pipe(
      map(resp => this.mapAccountsPayable(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar conta a pagar');
        return throwError(() => error);
      })
    );
  }

  /**
   * GET /accountspayable/{id}/attachments/{attachmentId}/download-url
   * Retorna URL assinada (SAS) para download.
   */
  getAttachmentDownloadUrl(
    accountsPayableId: number,
    attachmentId: number
  ): Observable<string> {
    return this.http
      .get<any>(this.financialUrl(`/${accountsPayableId}/attachments/${attachmentId}/download-url`))
      .pipe(
        map(res => this.normalizeDownloadUrlResponse(res)),
        catchError(error => {
          this.errorService.showErrorMessage('Erro ao gerar URL de download do comprovante');
          return throwError(() => error);
        })
      );
  }

  /**
   * GET /accountspayable/{id}/attachments/{attachmentId}/download
   * Baixa o arquivo como blob.
   */
  downloadAttachment(accountsPayableId: number, attachmentId: number): Observable<Blob> {
    return this.http
      .get(this.financialUrl(`/${accountsPayableId}/attachments/${attachmentId}/download`), {
        responseType: 'blob'
      })
      .pipe(
        catchError(error => {
          this.errorService.showErrorMessage('Erro ao baixar comprovante');
          return throwError(() => error);
        })
      );
  }

  uploadAttachment(
    accountsPayableId: number,
    file: File,
    options?: { category?: string; uploadedBy?: string }
  ): Observable<AccountsPayableAttachment> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    let params = new HttpParams();
    if (options?.category) {
      params = params.set('category', options.category);
    }
    if (options?.uploadedBy) {
      params = params.set('uploadedBy', options.uploadedBy);
    }

    return this.http
      .post<any>(this.financialUrl(`/${accountsPayableId}/attachments`), formData, { params })
      .pipe(
        map(resp => this.mapAttachment(resp)),
        catchError(error => {
          this.errorService.showErrorMessage('Erro ao anexar comprovante');
          return throwError(() => error);
        })
      );
  }

  getAccountsPayableByDocumentNumber(documentNumber: string): Observable<AccountsPayable> {
    return this.http.get<AccountsPayable>(this.financialUrl(`/document/${documentNumber}`)).pipe(
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar conta a pagar por documento');
        return throwError(() => error);
      })
    );
  }

  getAllAccountsPayable(filters?: AccountsPayableFilters): Observable<AccountsPayablePagedResult> {
    let params = new HttpParams();
    
    params = params
      .set('page', String(filters?.page ?? 1))
      .set('pageSize', String(filters?.pageSize ?? 10));
    
    // Parâmetros de filtro
    if (filters?.supplierId) params = params.set('supplierId', filters.supplierId.toString());
    if (filters?.status) params = params.set('status', filters.status);
    if (filters?.costCenterId) params = params.set('costCenterId', filters.costCenterId.toString());
    if (filters?.category) params = params.set('category', filters.category);
    if (filters?.dueDateStart) params = params.set('dueDateStart', filters.dueDateStart);
    if (filters?.dueDateEnd) params = params.set('dueDateEnd', filters.dueDateEnd);
    if (filters?.paymentDateStart) params = params.set('paymentDateStart', filters.paymentDateStart);
    if (filters?.paymentDateEnd) params = params.set('paymentDateEnd', filters.paymentDateEnd);
    
    // Busca por termo (aceita searchTerm ou search conforme documentação)
    if (filters?.searchTerm) {
      params = params.set('searchTerm', filters.searchTerm);
    } else if (filters?.search) {
      params = params.set('searchTerm', filters.search);
    }
    
    return this.http.get<any>(this.financialUrl(), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar contas a pagar');
        return throwError(() => error);
      })
    );
  }

  updateAccountsPayable(accountsPayable: AccountsPayableUpdate): Observable<AccountsPayable> {
    return this.http.put<any>(this.financialUrl(`/${accountsPayable.id}`), accountsPayable).pipe(
      map(resp => this.mapAccountsPayable(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao atualizar conta a pagar');
        return throwError(() => error);
      })
    );
  }

  deleteAccountsPayable(id: number): Observable<void> {
    return this.http.delete<void>(this.financialUrl(`/${id}`)).pipe(
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao deletar conta a pagar');
        return throwError(() => error);
      })
    );
  }

  // ===== FILTER OPERATIONS =====

  getPendingPayments(options?: PaginationOptions): Observable<AccountsPayablePagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
  return this.http.get<any>(this.financialUrl('/pending'), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar pagamentos pendentes');
        return throwError(() => error);
      })
    );
  }

  getOverduePayments(options?: PaginationOptions): Observable<AccountsPayablePagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
  return this.http.get<any>(this.financialUrl('/overdue'), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar pagamentos vencidos');
        return throwError(() => error);
      })
    );
  }

  getAccountsPayableBySupplier(supplierId: number, options?: PaginationOptions): Observable<AccountsPayablePagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
  return this.http.get<any>(this.financialUrl(`/supplier/${supplierId}`), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar contas por fornecedor');
        return throwError(() => error);
      })
    );
  }

  getAccountsPayableByStatus(status: string, options?: PaginationOptions): Observable<AccountsPayablePagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
  return this.http.get<any>(this.financialUrl(`/status/${status}`), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar contas por status');
        return throwError(() => error);
      })
    );
  }

  getAccountsPayableByCategory(category: string, options?: PaginationOptions): Observable<AccountsPayablePagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
  return this.http.get<any>(this.financialUrl(`/category/${category}`), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar contas por categoria');
        return throwError(() => error);
      })
    );
  }

  // ===== SUMMARY OPERATIONS =====

  getTotalPendingAmount(): Observable<{ totalPendingAmount: number }> {
    return this.http.get<{ totalPendingAmount: number }>(this.financialUrl('/total-pending')).pipe(
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar total pendente');
        return throwError(() => error);
      })
    );
  }

  getTotalOverdueAmount(): Observable<{ totalOverdueAmount: number }> {
    return this.http.get<{ totalOverdueAmount: number }>(this.financialUrl('/total-overdue')).pipe(
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar total em atraso');
        return throwError(() => error);
      })
    );
  }

  // ===== VALIDATION =====

  checkDocumentNumberExists(documentNumber: string, excludeId?: number): Observable<{ exists: boolean }> {
    let params = new HttpParams();
    if (excludeId) {
      params = params.set('excludeId', excludeId.toString());
    }
    
  return this.http.get<{ exists: boolean }>(this.financialUrl(`/check-document/${documentNumber}`), { params }).pipe(
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao verificar documento');
        return throwError(() => error);
      })
    );
  }

  // ===== SEARCH OPERATIONS =====

  searchAccountsPayable(term: string): Observable<AccountsPayable[]> {
    const params = new HttpParams().set('search', term);
  return this.http.get<AccountsPayable[]>(this.financialUrl('/search'), { params }).pipe(
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar contas a pagar');
        return throwError(() => error);
      })
    );
  }

  // ===== FILTER WITH PARAMS =====

  getAccountsPayableWithFilters(filters: AccountsPayableFilter): Observable<AccountsPayable[]> {
    let params = new HttpParams();
    
    if (filters.supplierId) params = params.set('supplierId', filters.supplierId.toString());
    if (filters.status) params = params.set('status', filters.status);
    if (filters.category) params = params.set('category', filters.category);
    if (filters.dueDateFrom) params = params.set('dueDateFrom', filters.dueDateFrom.toString());
    if (filters.dueDateTo) params = params.set('dueDateTo', filters.dueDateTo.toString());
    if (filters.amountFrom) params = params.set('amountFrom', filters.amountFrom.toString());
    if (filters.amountTo) params = params.set('amountTo', filters.amountTo.toString());

  return this.http.get<AccountsPayable[]>(this.financialUrl('/filter'), { params }).pipe(
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar contas com filtros');
        return throwError(() => error);
      })
    );
  }

  // ===== RANGE AND RELATIONS =====

  getByDueDateRange(start: string, end: string, options?: PaginationOptions): Observable<AccountsPayablePagedResult> {
    let params = new HttpParams().set('start', start).set('end', end);
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
  return this.http.get<any>(this.financialUrl('/due-range'), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar por faixa de vencimento');
        return throwError(() => error);
      })
    );
  }

  getByPaymentDateRange(start: string, end: string, options?: PaginationOptions): Observable<AccountsPayablePagedResult> {
    let params = new HttpParams().set('start', start).set('end', end);
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
  return this.http.get<any>(this.financialUrl('/payment-range'), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar por faixa de pagamento');
        return throwError(() => error);
      })
    );
  }

  getByCostCenter(costCenterId: number, options?: PaginationOptions): Observable<AccountsPayablePagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
  return this.http.get<any>(this.financialUrl(`/costcenter/${costCenterId}`), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar por centro de custo');
        return throwError(() => error);
      })
    );
  }

  getByPurchaseOrderNumber(purchaseOrderNumber: string, options?: PaginationOptions): Observable<AccountsPayablePagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
  return this.http.get<any>(this.financialUrl(`/purchase-order/${encodeURIComponent(purchaseOrderNumber)}`), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar por pedido de compra');
        return throwError(() => error);
      })
    );
  }

  getByInvoiceNumber(invoiceNumber: string, options?: PaginationOptions): Observable<AccountsPayablePagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
  return this.http.get<any>(this.financialUrl(`/invoice/${encodeURIComponent(invoiceNumber)}`), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar por nota fiscal');
        return throwError(() => error);
      })
    );
  }

  private mapPagedResponse(response: any): AccountsPayablePagedResult {
    const rawItems = Array.isArray(response?.Items)
      ? response.Items
      : Array.isArray(response?.items)
        ? response.items
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];

    const items = rawItems.map((item: any) => this.mapAccountsPayable(item));

    const page = response?.Page ?? response?.page ?? 1;
    const pageSize = response?.PageSize ?? response?.pageSize ?? items.length;
    const totalItems = response?.TotalItems ?? response?.totalItems ?? response?.TotalRecords ?? response?.totalRecords ?? items.length;
    const totalPages = response?.TotalPages ?? response?.totalPages ?? (pageSize > 0 ? Math.ceil(totalItems / pageSize) : 1);

    return {
      items,
      page,
      pageSize,
      totalItems,
      totalPages,
    };
  }

  private mapAccountsPayable(raw: any): AccountsPayable {
    if (!raw) return raw;
    const attachmentsRaw = raw.attachments ?? raw.Attachments ?? [];
    return {
      ...raw,
      id: raw.id ?? raw.Id,
      documentNumber: raw.documentNumber ?? raw.DocumentNumber,
      documentType: raw.documentType ?? raw.DocumentType,
      issueDate: raw.issueDate ?? raw.IssueDate,
      dueDate: raw.dueDate ?? raw.DueDate,
      paymentDate: raw.paymentDate ?? raw.PaymentDate,
      originalAmount: raw.originalAmount ?? raw.OriginalAmount,
      discountAmount: raw.discountAmount ?? raw.DiscountAmount,
      interestAmount: raw.interestAmount ?? raw.InterestAmount,
      fineAmount: raw.fineAmount ?? raw.FineAmount,
      amountToPay: raw.amountToPay ?? raw.AmountToPay,
      amountPaid: raw.amountPaid ?? raw.AmountPaid,
      status: raw.status ?? raw.Status,
      category: raw.category ?? raw.Category,
      paymentMethod: raw.paymentMethod ?? raw.PaymentMethod,
      paymentReference: raw.paymentReference ?? raw.PaymentReference,
      supplierId: raw.supplierId ?? raw.SupplierId,
      supplierName: raw.supplierName ?? raw.SupplierName,
      costCenterId: raw.costCenterId ?? raw.CostCenterId,
      costCenterName: raw.costCenterName ?? raw.CostCenterName,
      remainingAmount: raw.remainingAmount ?? raw.RemainingAmount,
      daysOverdue: raw.daysOverdue ?? raw.DaysOverdue ?? 0,
      attachments: Array.isArray(attachmentsRaw)
        ? attachmentsRaw.map((a: any) => this.mapAttachment(a))
        : []
    };
  }

  private normalizeDownloadUrlResponse(res: any): string {
    const data = res?.data ?? res?.Data ?? res?.result ?? res?.Result;
    const url =
      res?.downloadUrl ??
      res?.url ??
      res?.DownloadUrl ??
      res?.Url ??
      data?.downloadUrl ??
      data?.url ??
      data?.DownloadUrl ??
      data?.Url;

    if (typeof url === 'string' && url.trim()) {
      return url.trim();
    }
    if (typeof res === 'string' && res.trim()) {
      return res.trim();
    }
    throw new Error('Resposta inválida ao gerar URL de download');
  }

  private mapAttachment(raw: any): AccountsPayableAttachment {
    const filePath = raw?.filePath ?? raw?.FilePath ?? raw?.blobUrl ?? raw?.BlobUrl ?? raw?.url ?? raw?.Url;
    const originalFileName =
      raw?.originalFileName ?? raw?.OriginalFileName ?? raw?.fileName ?? raw?.FileName ?? '';
    return {
      id: raw?.id ?? raw?.Id,
      accountsPayableId: raw?.accountsPayableId ?? raw?.AccountsPayableId,
      fileName: raw?.fileName ?? raw?.FileName ?? originalFileName,
      originalFileName,
      filePath,
      blobUrl: filePath,
      category: raw?.category ?? raw?.Category,
      uploadedBy: raw?.uploadedBy ?? raw?.UploadedBy,
      uploadDate: raw?.uploadDate ?? raw?.UploadDate ?? raw?.createdAt ?? raw?.CreatedAt,
      contentType: raw?.contentType ?? raw?.ContentType,
      fileSize: raw?.fileSize ?? raw?.FileSize
    };
  }
}
